/**
 * The mock's half of `PATCH /api/emails/{id}/readStatus` and of the reply's
 * `inReplyToEmailId` (cynque-api-server #1814).
 *
 * ⚠ Written from the issue, ahead of the server. Check it against what lands.
 *
 * Deliberately NOT built on canChangeEmailReadStatus (the webapp's half, in
 * services/selectLists/emailReadStatii.ts): a mock that shares the client's
 * rule cannot catch a mistake in it. Here the repeat of the current value is
 * ALLOWED, as the server allows it -- two people opening one message at once --
 * where the client never offers it.
 */

// Literal ids, for the same reason: emailreadstatuses as the server seeds it.
export const READ_STATUS = { UNREAD: 1, READ: 2, REPLIED: 3 } as const

const LABELS: Record<number, string> = {
  [READ_STATUS.UNREAD]: 'Unread',
  [READ_STATUS.READ]: 'Read',
  [READ_STATUS.REPLIED]: 'Replied',
}

export function readStatusItem(id: number) {
  return { id, label: LABELS[id] ?? '' }
}

interface IReadStatusTarget {
  direction?: 'inbound' | 'outbound' | null
  readStatus?: { id: unknown } | null
}

/**
 * Why a hand-made change is refused, or null when it is allowed. Allowed:
 * Unread -> Read, Read -> Unread, and any value to itself except Replied.
 */
export function readStatusChangeRefusal(
  email: IReadStatusTarget,
  toId: unknown
): string | null {
  if (email.direction !== 'inbound') {
    return 'Only an inbound email has a read status.'
  }
  if (toId === READ_STATUS.REPLIED) {
    return 'Replied is set by sending a reply, not by hand.'
  }
  if (toId !== READ_STATUS.UNREAD && toId !== READ_STATUS.READ) {
    return `${String(toId)} is not a read status.`
  }
  if (email.readStatus?.id === READ_STATUS.REPLIED) {
    return 'A replied email keeps its read status.'
  }
  return null
}

/**
 * Why a reply's `inReplyToEmailId` is refused, or null. It must name an
 * inbound email on the case being replied on.
 */
export function inReplyToRefusal(
  target: (IReadStatusTarget & { caseId?: string }) | undefined,
  caseId: string
): string | null {
  if (!target || target.caseId !== caseId) {
    return 'inReplyToEmailId is not an email on this case.'
  }
  if (target.direction !== 'inbound') {
    return 'inReplyToEmailId must name an inbound email.'
  }
  return null
}
